import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Lock, Sparkles, ArrowLeft } from "lucide-react";
import { useEntitlement } from "../lib/entitlement";
import { isLoadError, isAuthenticated, getUpgradeMessage } from "../lib/featureAccess";

/**
 * UpgradeScreen — full-page upgrade UI used when:
 *   - A locked route is opened directly by URL.
 *   - A locked tile click navigates here.
 *   - An action-level guard surfaces a non-recoverable lock.
 *
 * "Manage plan" appears only for signed-in users (the typical path is
 * View Pricing -> Subscribe). Internal tier names never appear.
 */
export default function UpgradeScreen({ title, context }) {
  const ent = useEntitlement();
  const navigate = useNavigate();
  const location = useLocation();
  const loadErr = isLoadError(ent);
  const signedIn = isAuthenticated(ent);

  // Recovery action. A locked route is usually reached by navigating AWAY
  // from an editor that may hold unsaved work (e.g. "Manage Bit library"
  // from the BHA editor), and the only exits here used to be /pricing and
  // /home — both of which discard that work. navigate(-1) returns to the
  // previous entry, and ScrollToTop deliberately skips its reset on POP, so
  // scroll position comes back with it.
  //
  // React Router gives the FIRST entry in a history stack the key
  // "default". When that is the key here, the paywall was opened directly
  // (deep link, or a print view opened via window.open into a fresh tab) —
  // there is no in-app page to return to, so the button is not offered and
  // the screen behaves exactly as it did before.
  const canGoBack = location.key !== "default";

  const heading = title || "This feature is available on RigSentinel Pro";
  const message = loadErr
    ? "Unable to verify your plan right now. If you just upgraded, your subscription may take a few seconds to activate — refresh in ~30 seconds. If the problem persists, please contact support."
    : getUpgradeMessage(context);

  return (
    <div className="flex flex-col gap-4" data-testid="upgrade-screen">
      <section className="card-rs p-5" data-testid="upgrade-screen-card">
        <div className="flex items-center gap-2">
          <Lock className="w-4 h-4" style={{ color: "var(--rs-amber)" }} />
          <div className="font-mono-rs text-[10px] tracking-[0.22em] uppercase text-[var(--rs-amber)]">
            RigSentinel Pro Required
          </div>
        </div>
        <h2
          className="font-heading font-black text-2xl tracking-tight mt-2"
          data-testid="upgrade-screen-title"
        >
          {heading}
        </h2>
        <p
          className="text-sm text-[var(--rs-text-2)] mt-1.5 leading-relaxed"
          data-testid="upgrade-screen-message"
        >
          {message}
        </p>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          {canGoBack && (
            <button
              type="button"
              onClick={() => navigate(-1)}
              data-testid="upgrade-screen-go-back"
              className="btn-ghost h-10 px-4 rounded-md text-xs label-up inline-flex items-center gap-1.5"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Go back
            </button>
          )}
          <Link
            to="/pricing"
            data-testid="upgrade-screen-view-plans"
            className="btn-amber h-10 px-4 rounded-md text-xs label-up inline-flex items-center gap-1.5"
          >
            <Sparkles className="w-3.5 h-3.5" /> View Pricing
          </Link>
          {signedIn && (
            <Link
              to="/pricing"
              data-testid="upgrade-screen-manage-plan"
              className="btn-ghost h-10 px-4 rounded-md text-xs label-up inline-flex items-center"
            >
              Manage plan
            </Link>
          )}
          <Link
            to="/home"
            data-testid="upgrade-screen-back-home"
            className="btn-ghost h-10 px-4 rounded-md text-xs label-up inline-flex items-center"
          >
            Back to Home
          </Link>
        </div>
      </section>
    </div>
  );
}
